import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { GridPager } from "../../components/AppDataGrid";
import { useNotify } from "../../components/notifications";
import { useSaveFlow } from "../../components/confirm";
import { useHotkeys, formatCombo } from "../../components/hotkeys";
import "./SettingsDetailPage.css"; // `.sd-*` chrome + `.sd-actionbar` / `.sd-abtn`
import "./SampleFieldConfig.css"; // `.sfc-*` dual-list styles

/**
 * Settings ▸ Flutter ▸ Flutter Template Builder — full-page detail (ALPACA
 * FlutterTemplateBuilderController / SDATemplate_DetailView parity): header
 * (Template ID · Template Name · Category · Visual Matrix · Is Active) + the
 * Station / Sampling / Test field-assignment dual-list.
 *
 * SELECTED fields = dbo.SDATemplateDetail (editable Caption · ISReadonly · Sort).
 * AVAILABLE = the master pool (dbo.tbl_FlutterSDA_FieldEntryColumn filtered by the
 * chosen Visual Matrix = Element) minus the already-selected fields, derived on the
 * client so changing the Visual Matrix refilters without disturbing unsaved edits.
 */

const TABS = ["Station", "Sampling", "Test"] as const;
type Tab = (typeof TABS)[number];

interface AvailField { fieldEntryColumnId: number; fieldName: string | null; caption: string | null; }
interface SelField {
  oid: string | null;
  fieldEntryColumnId: number;
  fieldName: string | null;
  caption: string | null;
  isReadonly: boolean;
  sort: number;
}
interface MatrixOption { value: number; label: string; }

interface Props {
  id: string | null;
  pageId?: string;
  title?: string;
  group?: string;
  onHome?: () => void;
  onSaved: (newId?: string) => void;
  onClose: () => void;
}

const API = "/api/v1/field-templates";
const emptyByTab = <T,>(): Record<Tab, T[]> => ({ Station: [], Sampling: [], Test: [] });

export function FlutterTemplateBuilderDetail({
  id, title, group, onHome, onSaved, onClose,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();

  // Internal record id (seeded from the prop) so Save keeps you on the saved record
  // and Save and New clears to a blank one.
  const [recordId, setRecordId] = useState<string | null>(id ?? null);
  useEffect(() => { setRecordId(id ?? null); }, [id]);

  const [loading, setLoading] = useState<boolean>(id != null);
  const [, setLoadMs] = useState<number | undefined>();  // retained to time the load; the chip was removed (owner request 2026-10-16)

  // Header
  const [templateIdText, setTemplateIdText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [category, setCategory] = useState("0");   // TemplateCategory: Field Entry = 0
  const [element, setElement] = useState(0);        // TemplateElement (Visual Matrix)
  const [isActive, setIsActive] = useState(false);
  const [matrices, setMatrices] = useState<MatrixOption[]>([]);

  // Field config
  const [activeTab, setActiveTab] = useState<Tab>("Station");
  const [poolByTab, setPoolByTab] = useState<Record<Tab, AvailField[]>>(emptyByTab);
  const [selByTab, setSelByTab] = useState<Record<Tab, SelField[]>>(emptyByTab);

  // Visual Matrix options (fixed enum, served from the API for one source of truth).
  useEffect(() => {
    api.get<{ data?: MatrixOption[] }>(`${API}/visual-matrices`)
      .then((r) => setMatrices(r.data.data ?? []))
      .catch(() => setMatrices([{ value: 0, label: "Stream" }, { value: 1, label: "Lake" }, { value: 2, label: "Liquid" }]));
  }, []);

  const setPools = (pools: { tab: string; pool: AvailField[] }[]) => {
    const next = emptyByTab<AvailField>();
    for (const p of pools) if ((TABS as readonly string[]).includes(p.tab)) next[p.tab as Tab] = p.pool ?? [];
    setPoolByTab(next);
  };

  // ── Load ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const t0 = performance.now();
    (async () => {
      try {
        if (recordId == null) {
          // New: blank header, default Visual Matrix (Stream), master pool for it.
          setTemplateIdText(""); setTemplateName(""); setCategory("0");
          setElement(0); setIsActive(false); setSelByTab(emptyByTab());
          const pr = await api.get<{ data?: { tab: string; pool: AvailField[] }[] }>(`${API}/field-pools`, { params: { element: 0 } });
          if (!alive) return;
          setPools(pr.data.data ?? []);
          setLoadMs(performance.now() - t0);
          return;
        }
        setLoading(true);
        const [hr, cr] = await Promise.all([
          api.get<{ data?: { templateId?: string; templateName?: string; category?: number; element?: number; isActive?: boolean } }>(`${API}/${recordId}`),
          api.get<{ data?: { element: number; tabs: { tab: string; selected: SelField[]; pool: AvailField[] }[] } }>(`${API}/${recordId}/field-config`),
        ]);
        if (!alive) return;
        const h = hr.data.data;
        setTemplateIdText(h?.templateId ?? "");
        setTemplateName(h?.templateName ?? "");
        setCategory(String(h?.category ?? 0));
        setIsActive(!!h?.isActive);
        const c = cr.data.data;
        setElement(c?.element ?? h?.element ?? 0);
        const sel = emptyByTab<SelField>(); const pool = emptyByTab<AvailField>();
        for (const t of c?.tabs ?? []) if ((TABS as readonly string[]).includes(t.tab)) {
          sel[t.tab as Tab] = t.selected ?? []; pool[t.tab as Tab] = t.pool ?? [];
        }
        setSelByTab(sel); setPoolByTab(pool);
        setLoadMs(performance.now() - t0);
      } catch {
        if (alive) notify.error("Could not load the template.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  // Visual Matrix change → fetch the new master pool (selected set untouched).
  const onElementChange = async (el: number) => {
    setElement(el);
    try {
      const pr = await api.get<{ data?: { tab: string; pool: AvailField[] }[] }>(`${API}/field-pools`, { params: { element: el } });
      setPools(pr.data.data ?? []);
    } catch {
      notify.error("Could not load the fields for that Visual Matrix.");
    }
  };

  // available(tab) = pool minus selected (by FieldName).
  const availFor = useCallback((tab: Tab): AvailField[] => {
    const taken = new Set(selByTab[tab].map((s) => (s.fieldName ?? "").toLowerCase()));
    return poolByTab[tab].filter((p) => !taken.has((p.fieldName ?? "").toLowerCase()));
  }, [poolByTab, selByTab]);

  const addField = (tab: Tab, f: AvailField) =>
    setSelByTab((prev) => {
      if (prev[tab].some((s) => s.fieldEntryColumnId === f.fieldEntryColumnId)) return prev;
      const nextSort = prev[tab].reduce((m, s) => Math.max(m, s.sort), -1) + 1;
      return { ...prev, [tab]: [...prev[tab], { oid: null, fieldEntryColumnId: f.fieldEntryColumnId, fieldName: f.fieldName, caption: f.caption, isReadonly: false, sort: nextSort }] };
    });

  const removeField = (tab: Tab, f: SelField) =>
    setSelByTab((prev) => ({ ...prev, [tab]: prev[tab].filter((s) => s.fieldEntryColumnId !== f.fieldEntryColumnId) }));

  const patchField = (tab: Tab, fec: number, patch: Partial<SelField>) =>
    setSelByTab((prev) => ({ ...prev, [tab]: prev[tab].map((s) => (s.fieldEntryColumnId === fec ? { ...s, ...patch } : s)) }));

  const clearForNew = () => {
    setRecordId(null); setTemplateIdText(""); setTemplateName(""); setCategory("0");
    setElement(0); setIsActive(false); setSelByTab(emptyByTab());
    void onElementChange(0);
  };

  const allFields = () =>
    TABS.flatMap((tab) => selByTab[tab].map((s) => ({
      oid: s.oid, fieldEntryColumnId: s.fieldEntryColumnId, tab, caption: s.caption, isReadonly: s.isReadonly, sort: s.sort,
    })));

  const doSave = async (after: "stay" | "close" | "new") => {
    if (!templateName.trim()) { notify.error("Template Name must not be empty."); return; }
    let savedId: string | undefined = recordId ?? undefined;
    await runSave({
      formName: "Field Template Builder",
      auditAction: recordId == null ? "create" : "edit",
      save: async () => {
        const header = { templateName: templateName.trim(), category, isActive, element };
        if (recordId == null) {
          const res = await api.post<{ data?: { oid?: number } }>(API, header);
          savedId = res.data.data?.oid != null ? String(res.data.data.oid) : undefined;
        } else {
          await api.put(`${API}/${recordId}`, header);
          savedId = recordId;
        }
        if (savedId) await api.put(`${API}/${savedId}/field-config`, { fields: allFields() });
      },
      onSaved: () => {
        if (after === "close") { onSaved(savedId); return; }
        if (after === "new") { clearForNew(); return; }
        setRecordId(savedId ?? null);   // 'stay' — a create reloads via recordId
      },
    });
  };

  useHotkeys(useMemo(() => [
    { combo: "mod+s", label: "Save", group: "Field Template Builder", order: 1, run: () => void doSave("stay") },
    { combo: "esc", label: "Cancel", group: "Field Template Builder", order: 2, allowInInput: false, run: () => onClose() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [templateName, category, element, isActive, selByTab, recordId]));

  const recordName = templateName.trim() || (recordId == null ? "New Field Template" : "Field Template");
  const dragRef = useRef<number | null>(null);

  const trioBar = (bottom?: boolean) => (
    <div className={`sd-actionbar${bottom ? " bottom" : ""}`}>
      <button className="sd-abtn primary" onClick={() => void doSave("stay")} title={`Save (${formatCombo("mod+s")})`}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="sd-abtn" onClick={() => void doSave("close")}><span className="tb-i">💾</span> Save and Close</button>
      <button className="sd-abtn" onClick={() => void doSave("new")}><span className="tb-i">📄</span> Save and New</button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={onClose} title={`Cancel (${formatCombo("esc")})`}><span className="tb-i">↩︎</span> Cancel</button>
    </div>
  );

  const available = availFor(activeTab);
  const selected = selByTab[activeTab];

  // Selected-grid paging (owner request 2026-10-16) — the shared <GridPager> footer.
  // Page size 20, so each tab (≤20 fields) shows on one page; the state lives OUTSIDE
  // the per-tab data, so add / remove / edit still act on the whole set.
  const [selPage, setSelPage] = useState(1);
  const [selPageSize, setSelPageSize] = useState(20);
  useEffect(() => { setSelPage(1); }, [activeTab]);
  const selTotalPages = Math.max(1, Math.ceil(selected.length / selPageSize));
  const selSafePage = Math.min(selPage, selTotalPages);
  const pagedSelected = selected.slice((selSafePage - 1) * selPageSize, selSafePage * selPageSize);

  return (
    <div className="bl-page mlv-host ftb-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div style={{ minWidth: 0 }}>
              {/* "{BO} – {record}" title + breadcrumb, matching the shared
                  SettingsDetailPage header (owner request 2026-10-16, image 740).
                  No load-time chip; audit is reached from the list toolbar. */}
              <h2>{(title || "Flutter Template Builder")} – {recordName}</h2>
              <div className="sd-bc">
                {onHome && (<><a onClick={onHome}>Home</a><i className="ti ti-chevron-right" /></>)}
                {group && (<><span>{group}</span><i className="ti ti-chevron-right" /></>)}
                {title && (<><a onClick={onClose}>{title}</a><i className="ti ti-chevron-right" /></>)}
                <span>{recordName}</span>
              </div>
            </div>
            {/* Prev/next record arrows — visual parity with the standard header;
                record navigation is not wired on this bespoke page (display-only). */}
            <div className="sd-navarrows">
              <button className="sd-na" title="Previous record" disabled>‹</button>
              <button className="sd-na next" title="Next record" disabled>›</button>
            </div>
          </div>
          {trioBar()}

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading">Loading…</div>
            ) : (
              <>
                {/* Header fields — Template ID (ro) · Template Name · Category · Visual Matrix · Is Active. */}
                <h3 className="sd-section-h">General Information</h3>
                <div className="ftb-form">
                  <label className="ftb-row">
                    <span className="ftb-lbl">Template ID:</span>
                    <input className="sd-input ftb-ro" value={templateIdText || (recordId == null ? "Autogenerate" : "")} readOnly disabled />
                  </label>
                  <label className="ftb-row">
                    <span className="ftb-lbl ftb-req">Template Name:<span className="ftb-star">*</span></span>
                    <input className="sd-input" value={templateName} maxLength={200} onChange={(e) => setTemplateName(e.target.value)} />
                  </label>
                  <label className="ftb-row">
                    <span className="ftb-lbl">Category:</span>
                    <select className="sd-input" value={category} onChange={(e) => setCategory(e.target.value)}>
                      <option value="0">Field Entry</option>
                    </select>
                  </label>
                  <label className="ftb-row">
                    <span className="ftb-lbl">Visual Matrix:</span>
                    <select className="sd-input" value={element} onChange={(e) => void onElementChange(Number(e.target.value))}>
                      {matrices.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </label>
                  <label className="ftb-row ftb-check">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    <span>Is Active</span>
                  </label>
                </div>

                {/* Station / Sampling / Test tabs + the available ↔ selected dual-list. */}
                <div className="sfc-tab-body">
                  <div className="sfc-tabs">
                    {TABS.map((t) => (
                      <button key={t} type="button" className={`sfc-tab${t === activeTab ? " active" : ""}`} onClick={() => setActiveTab(t)}>
                        {t} <span className="sfc-tab-count">{selByTab[t].length}</span>
                      </button>
                    ))}
                  </div>
                  <div className="sfc-panels">
                    {/* Available (left) */}
                    <div className="sfc-panel">
                      <div className="sfc-panel-head">Available <span className="sfc-tab-count">{available.length}</span></div>
                      <div className="sfc-avail-colhead"><span className="sfc-avail-name">FieldName</span><span className="sfc-avail-cap">Caption</span><span style={{ flex: "0 0 18px" }} aria-hidden /></div>
                      <div className="sfc-avail">
                        {available.length === 0 && <div className="sfc-empty">No more fields</div>}
                        {available.map((a) => (
                          <div key={a.fieldEntryColumnId} className="sfc-avail-row sfc-draggable" draggable
                            onDragStart={(e) => { dragRef.current = a.fieldEntryColumnId; e.dataTransfer.effectAllowed = "move"; }}
                            onDragEnd={() => { dragRef.current = null; }}
                            title="Drag into Selected, or click +">
                            <span className="sfc-avail-name">{a.fieldName}</span>
                            <span className="sfc-avail-cap" title={a.caption ?? ""}>{a.caption ?? ""}</span>
                            <button className="sfc-add" title="Add" onClick={() => addField(activeTab, a)}><i className="ti ti-plus" /></button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selected (right) — drop target */}
                    <div className="sfc-panel sfc-panel-selected"
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fec = dragRef.current;
                        const a = available.find((x) => x.fieldEntryColumnId === fec);
                        if (a) addField(activeTab, a);
                      }}>
                      <div className="sfc-panel-head">Selected <span className="sfc-tab-count">{selected.length}</span></div>
                      <div className="sfc-sel-scroll">
                        <table className="sfc-sel-table">
                          <thead>
                            <tr><th></th><th>FieldName</th><th>Caption</th><th>ISReadonly</th><th>Sort</th></tr>
                          </thead>
                          <tbody>
                            {selected.length === 0 && (
                              <tr><td colSpan={5} className="sfc-empty">No fields selected — add from the left.</td></tr>
                            )}
                            {pagedSelected.map((r) => (
                              <tr key={r.fieldEntryColumnId}>
                                <td><button className="sfc-remove" title="Remove" onClick={() => removeField(activeTab, r)}><i className="ti ti-minus" /></button></td>
                                <td className="sfc-name-cell">{r.fieldName}</td>
                                <td><input className="sfc-cell-input" value={r.caption ?? ""} onChange={(e) => patchField(activeTab, r.fieldEntryColumnId, { caption: e.target.value })} /></td>
                                <td className="sfc-cell-center"><input type="checkbox" checked={r.isReadonly} onChange={(e) => patchField(activeTab, r.fieldEntryColumnId, { isReadonly: e.target.checked })} /></td>
                                <td><input type="text" inputMode="numeric" className="sfc-cell-num" value={r.sort} onChange={(e) => patchField(activeTab, r.fieldEntryColumnId, { sort: Number(e.target.value) || 0 })} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <GridPager
                        total={selected.length}
                        page={selSafePage}
                        pageSize={selPageSize}
                        onPageChange={setSelPage}
                        onPageSizeChange={(n) => { setSelPageSize(n); setSelPage(1); }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {trioBar(true)}
        </div>
      </div>
      <style>{FTB_CSS}</style>
    </div>
  );
}

const FTB_CSS = `
  .ftb-form { max-width: 560px; display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }
  .ftb-row { display: flex; align-items: center; gap: 10px; }
  .ftb-lbl { width: 130px; flex: 0 0 130px; font-size: 13px; color: var(--ai-ink, #1f2430); }
  .ftb-req { color: #c0392b; }
  .ftb-star { color: #c0392b; margin-left: 2px; }
  /* De-yellowed (plain white) + square corners for the header controls (owner
     request 2026-10-16); the read-only Template ID keeps the grey cue. */
  .ftb-row .sd-input { flex: 1 1 auto; height: 28px; background: #fff; border-radius: 0; }
  .ftb-row .sd-input.ftb-ro { background: var(--ai-readonly-bg, #f5f6f8); color: var(--ai-muted); }
  /* Is Active indented to the FIELD column (owner request 2026-10-16, image 746):
     130px label + 10px row gap, so the checkbox lines up under the inputs. */
  .ftb-check { gap: 7px; padding-left: 140px; }
  .ftb-check input { margin: 0; }
  /* Dual-list +/- = the shared SOLID green + / red - circle with a WHITE glyph (.sfc-add / .sfc-remove in SampleFieldConfig.css,
     the QC Batch standard; owner request 2026-11-03 - this form used to override it with the 2026-10-16 outline look). */
  /* Frame the whole tab control (tabs + Available/Selected) in ONE border
     (owner request 2026-10-16, image 744). */
  .ftb-detail .sfc-tab-body { border: 1px solid var(--ai-line, #e3e6ec); border-radius: 8px; padding: 12px; background: #fff; }
  /* Selected grid = standard compact rows (owner request 2026-10-16 — "rows looking
     differently"): 22px in-cell editors + 2px cell padding → the shared 26px row. */
  .ftb-detail .sfc-sel-table th { padding: 5px 10px; }
  .ftb-detail .sfc-sel-table td { padding: 2px 10px; }
  .ftb-detail .sfc-cell-input, .ftb-detail .sfc-cell-num { padding: 2px 6px; height: 22px; }
  /* Available list gains a Caption column (owner request 2026-10-16, image 748) —
     FieldName | Caption | + button, instead of just FieldName. */
  .ftb-detail .sfc-avail-row, .ftb-detail .sfc-avail-colhead { justify-content: flex-start; gap: 8px; }
  .ftb-detail .sfc-avail-name { flex: 1 1 0; }
  .ftb-detail .sfc-avail-cap { flex: 1 1 0; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; color: var(--ai-ink, #1f2430); }
  .ftb-detail .sfc-avail-colhead .sfc-avail-cap { font-weight: 700; }
  .ftb-detail .sfc-avail-row .sfc-add { flex: 0 0 auto; }
`;
